import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';

const formatSalary = (min, max) => {
  const f = (v) => new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
  if (min && max) return `${f(min)} – ${f(max)}`;
  if (min) return `${f(min)}+`;
  if (max) return `≤ ${f(max)}`;
  return null;
};

function Meta({ icon, children }) {
  if (!children) return null;
  return (
    <div className="flex items-center gap-1.5 text-sm text-gray-600">
      <Icon name={icon} className="h-4 w-4 shrink-0 text-gray-400" />
      {children}
    </div>
  );
}

export default function JobCard({ position }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'id';

  const localized = (field) =>
    position[`${field}_${lang}`] || position[`${field}_id`] || position[`${field}_en`] || '';

  const salary = formatSalary(position.salary_min, position.salary_max);

  const deadline = position.apply_deadline
    ? new Date(position.apply_deadline).toLocaleDateString(lang === 'en' ? 'en-GB' : 'id-ID', {
      day: 'numeric', month: 'long', year: 'numeric',
    })
    : null;
  const closingSoon = position.apply_deadline
    && (new Date(position.apply_deadline) - Date.now()) / 86400000 <= 7;

  return (
    <div className="card flex h-full flex-col p-6 transition-shadow duration-200 hover:shadow-lg">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-gray-900">{position.title}</h3>
          {position.branches?.name && (
            <p className="mt-0.5 text-sm text-gray-500">{position.branches.name}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {position.dorm_available && (
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
              {t('careers.dormAvailable')}
            </span>
          )}
          {position.quantity > 1 && (
            <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              {t('careers.slots', { count: position.quantity, defaultValue: '{{count}} posisi' })}
            </span>
          )}
        </div>
      </div>

      {localized('description') && (
        <p className="mb-4 text-sm leading-relaxed text-gray-600">{localized('description')}</p>
      )}

      <div className="mb-4 flex flex-wrap gap-x-5 gap-y-2">
        <Meta icon="mapPin">{position.region}</Meta>
        <Meta icon="wallet">{salary}</Meta>
        <Meta icon="clock">{position.schedule_notes}</Meta>
        <Meta icon="calendar">
          {deadline && (
            <span className={closingSoon ? 'font-semibold text-red-600' : ''}>
              {t('careers.until', 'Sampai')} {deadline}
            </span>
          )}
        </Meta>
      </div>

      <Link
        to={`/careers/apply/${position.id}`}
        className="btn-primary mt-auto inline-block w-full text-center"
      >
        {t('careers.applyNow')}
      </Link>
    </div>
  );
}
